import { Link } from "react-router-dom";
import { LifeBuoy, Ticket, Smartphone, HandCoins, Gift, Mail, QrCode } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Card, CardContent } from "../components/ui/card";
import { useLang } from "../context/LanguageContext";

// «Slik henter du billett» – kort steg-for-steg-hjelpeside for publikum (brukarval: berre steg, ingen FAQ).
// Bare faste tekster (bokmål som nøkkel, nynorsk i i18n). Dekker begge veiene: Betal med Vipps og Reserver billetter
// (betal ved inngangen) – pluss gratisbilletter. Lenker: hjelpeknapp + lenke i kjøpskortet på arrangementssiden, footer.

const Step = ({ n, icon: Icon, title, children, testId }) => (
  <li className="flex gap-4" data-testid={testId}>
    <span className="flex-none inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink text-ink-foreground font-heading font-semibold text-sm" aria-hidden="true">
      {n}
    </span>
    <div className="min-w-0 flex-1 space-y-2">
      <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
        <Icon className="w-4.5 h-4.5 text-muted-foreground" aria-hidden="true" />
        {title}
      </h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
    </div>
  </li>
);

const PathCard = ({ icon: Icon, title, children, highlight = false, testId, className = "" }) => (
  <div
    className={`rounded-xl border p-4 space-y-1.5 ${highlight ? "border-brand/50 bg-brand-soft/30" : "border-border bg-secondary/40"} ${className}`}
    data-testid={testId}
  >
    <p className="text-sm font-medium text-foreground flex items-center gap-2">
      <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
      {title}
    </p>
    <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
  </div>
);


export default function HelpPage() {
  const { t } = useLang();
  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="help-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs text-muted-foreground mb-4" data-testid="help-badge">
            <LifeBuoy className="w-3.5 h-3.5" />
            {t("Hjelp for publikum")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight" data-testid="help-title">
            {t("Slik henter du billett")}
          </h1>
          <p className="text-muted-foreground mt-2" data-testid="help-intro">
            {t("Ingen app og ingen konto – du trenger bare e-postadressen din og mobilen ved inngangen. Det tar under ett minutt.")}
          </p>
        </div>

        <Card className="border-border">
          <CardContent className="p-6 sm:p-8">
            <ol className="space-y-8" data-testid="help-steps">
              <Step n={1} icon={Ticket} title={t("Velg billetter")} testId="help-step-choose">
                <p>
                  {t("Åpne arrangementet – fra forsiden, en delt lenke eller ved å skanne QR-plakaten – og velg antall per billettype. Fyll inn navn, e-post og telefonnummer. Har du en rabattkode, legger du den inn her.")}
                </p>
              </Step>

              <Step n={2} icon={Smartphone} title={t("Betal med Vipps – eller reserver")} testId="help-step-pay">
                <p>{t("Prisen du ser, er prisen du betaler – Right in legger ikke på gebyr. Du har inntil tre måter å få billetten på, avhengig av arrangementet:")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <PathCard icon={Smartphone} title={t("Betal med Vipps")} highlight testId="help-path-vipps">
                    {t("Trykk «Betal … kr med Vipps». Du sendes til Vipps, godkjenner betalingen i appen og kommer rett tilbake til billettene dine – betalt og klare med en gang.")}
                  </PathCard>
                  <PathCard icon={HandCoins} title={t("Reserver billetter – betal ved inngangen")} highlight testId="help-path-door">
                    {t("Når arrangøren tilbyr det, velger du «Reserver billetter». Du får QR-billetten med en gang, merket «ikke betalt», og betaler beløpet til arrangøren når du kommer (kontant eller Vipps på stedet).")}
                  </PathCard>
                  <PathCard icon={Gift} title={t("Gratis arrangement")} testId="help-path-free" className="sm:col-span-2">
                    {t("Er arrangementet gratis, trykker du bare «Hent billetter». Ingen betaling – billettene er klare med en gang.")}
                  </PathCard>
                </div>
              </Step>

              <Step n={3} icon={Mail} title={t("Billettene kommer med en gang")} testId="help-step-receive">
                <p>
                  {t("Billettene vises på skjermen og sendes til e-posten din sammen med en egen billettlenke og PDF. Ta vare på lenken – den åpner billettene igjen når som helst, på hvilken som helst mobil. Du får også en påminnelse på e-post dagen før.")}
                </p>
              </Step>

              <Step n={4} icon={QrCode} title={t("Vis QR-koden ved inngangen")} testId="help-step-entry">
                <p>
                  {t("Åpne billettlenken (eller PDF-en/skjermbildet) på mobilen og vis QR-koden til den som sjekker inn. Hver billett har sin egen kode og kan bare skannes én gang. Det er fri plassering – ingen setenummer. Har du «ikke betalt»-billett, betaler du til arrangøren i døren først.")}
                </p>
              </Step>
            </ol>
          </CardContent>
        </Card>

        <p className="mt-6 text-sm text-muted-foreground leading-relaxed" data-testid="help-contact">
          {t("Spørsmål om billetter, betaling eller selve arrangementet besvares av arrangøren – kontaktinformasjonen står på arrangementssiden. Får du tekniske problemer med Right in, send en e-post til")}{" "}
          <a href="mailto:hei@rightin.no" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="help-contact-email">
            hei@rightin.no
          </a>
          .
        </p>

        <p className="text-xs text-muted-foreground mt-6 flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/" className="underline underline-offset-4" data-testid="help-home-link">{t("Tilbake til forsiden")}</Link>
          <Link to="/vilkar" className="underline underline-offset-4" data-testid="help-terms-link">{t("Vilkår for kjøp")}</Link>
          <Link to="/personvern" className="underline underline-offset-4" data-testid="help-privacy-link">{t("Personvern og GDPR")}</Link>
          <Link to="/om-oss" className="underline underline-offset-4" data-testid="help-about-link">{t("Om oss")}</Link>
        </p>
      </div>
    </PublicLayout>
  );
}
